// The four story tiles: Quality At-Bats · Free-Base Economy · Clutch ·
// Contact & Discipline. Every value renders through fmtStat so pre-extension
// imports show "—" with a re-import nudge, never a fake zero.

import React from 'react';
import { GS } from '../../lib/designTokens';
import { fmtStat } from '../../lib/stat-format';
import { Card } from './ui';
import type { SeasonData } from './useSeasonStats';

const Tile = ({ label, children, story }: { label: string; children: React.ReactNode; story?: React.ReactNode }) => (
  <Card className="p-4 flex flex-col">
    <p className="text-[9px] font-black uppercase" style={{ color: GS.ink3, letterSpacing: '0.12em' }}>{label}</p>
    <div className="flex-1">{children}</div>
    {story && (
      <p className="text-[10px] leading-relaxed font-semibold mt-2.5" style={{ color: GS.ink2 }}>{story}</p>
    )}
  </Card>
);

const QabRing = ({ pct }: { pct: number }) => {
  const C = 2 * Math.PI * 26;
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" role="img" aria-label={`Quality at-bat rate ${Math.round(pct * 100)} percent`}>
      <circle cx="32" cy="32" r="26" fill="none" stroke={GS.panel2} strokeWidth="8" />
      <circle
        cx="32" cy="32" r="26" fill="none" stroke="#0EA372" strokeWidth="8" strokeLinecap="round"
        strokeDasharray={`${(C * Math.min(pct, 1)).toFixed(1)} ${C.toFixed(1)}`}
        transform="rotate(-90 32 32)"
      />
      <text x="32" y="37" textAnchor="middle" fontSize="14" fontWeight="900" fill={GS.ink}>
        {fmtStat(pct, 'pct')}
      </text>
    </svg>
  );
};

const FreeBaseBar = ({ label, value, max, color }: { label: string; value: number; max: number; color: string }) => (
  <div className="mt-2.5">
    <div className="flex justify-between text-[10px] font-extrabold" style={{ color: GS.ink }}>
      <span>{label}</span>
      <span style={{ color }}>{value}</span>
    </div>
    <div className="h-2.5 rounded mt-1 overflow-hidden" style={{ background: GS.panel2 }}>
      <div className="h-full rounded" style={{ width: `${max > 0 ? Math.round((value / max) * 100) : 0}%`, background: color }} />
    </div>
  </div>
);

export const StoryTiles = ({ data }: { data: SeasonData }) => {
  const t = data.teamAgg;
  const maxFb = Math.max(t.freeBasesTaken, t.freeBasesGiven, 1);
  const clutchDelta = t.baRisp != null && t.avg != null ? Math.round((t.baRisp - t.avg) * 1000) : null;

  return (
    <div className="grid grid-cols-2 gap-3">
      <Tile
        label="Quality At-Bats"
        story={t.qab != null
          ? <>Winning at-bats, even when the box score isn't.</>
          : <>Re-import your latest GameChanger CSV to unlock quality at-bat tracking.</>}
      >
        {t.qabPct != null ? (
          <div className="flex items-center gap-3 mt-3">
            <QabRing pct={t.qabPct} />
            <div>
              <div className="text-2xl font-black leading-none" style={{ color: GS.ink }}>{fmtStat(t.qab, 'count')}</div>
              <div className="text-[10px] font-bold mt-1" style={{ color: GS.ink2 }}>of {t.pa} PA</div>
            </div>
          </div>
        ) : (
          <div className="text-2xl font-black mt-4" style={{ color: GS.ink3 }}>—</div>
        )}
      </Tile>

      <Tile
        label="Walks & HBP"
        story={(() => {
          // The tile name states exactly what is counted, so the old caveat
          // about steals sitting outside the comparison is no longer needed:
          // nobody reading "Walks & HBP" wonders where the steals went. Steals
          // are still captured (sbAllowed / csAllowed) for a running-game read
          // of their own; they simply do not belong on a walks chart.
          const diff = t.freeBasesTaken - t.freeBasesGiven;
          if (diff === 0) return <>Dead even.</>;
          return diff > 0
            ? <><b style={{ color: GS.ink }}>{diff} more taken than given.</b></>
            : <><b style={{ color: GS.ink }}>{-diff} more given than taken.</b></>;
        })()}
      >
        <FreeBaseBar label="Taken" value={t.freeBasesTaken} max={maxFb} color="#0EA372" />
        <FreeBaseBar label="Given" value={t.freeBasesGiven} max={maxFb} color={GS.red} />
      </Tile>

      <Tile
        label="Clutch"
        story={clutchDelta != null && clutchDelta > 0
          ? <>This team hits <b style={{ color: GS.ink }}>better with runners on</b>. Keep the green light on.</>
          : undefined}
      >
        <div className="flex gap-4 mt-3">
          <div>
            <div className="text-xl font-black leading-none" style={{ color: GS.ink }}>{fmtStat(t.baRisp, 'avg3')}</div>
            <div className="text-[9px] font-extrabold uppercase mt-1" style={{ color: GS.ink3, letterSpacing: '0.08em' }}>BA w/ RISP</div>
          </div>
          <div>
            <div className="text-xl font-black leading-none" style={{ color: GS.ink }}>{fmtStat(t.twoOutRbi, 'count')}</div>
            <div className="text-[9px] font-extrabold uppercase mt-1" style={{ color: GS.ink3, letterSpacing: '0.08em' }}>2-Out RBI</div>
          </div>
        </div>
        {clutchDelta != null && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-black rounded-lg px-2 py-0.5 mt-2"
            style={clutchDelta >= 0
              ? { background: GS.greenSoft, color: GS.greenInk }
              : { background: 'rgba(229,72,77,0.08)', color: GS.red }}
          >
            {clutchDelta >= 0 ? '▲' : '▼'} {Math.abs(clutchDelta)} pts vs season AVG
          </span>
        )}
      </Tile>

      <Tile
        label="Contact & Discipline"
        story={t.kLookingShare != null
          ? <><b style={{ color: GS.ink }}>{fmtStat(t.kLookingShare, 'pct')} of strikeouts end with the bat on the shoulder.</b> An aggression cue for practice.</>
          : undefined}
      >
        {/* A stacked list, not three boxes across. Three side-by-side tiles in
            a half-width card leaves each about 50px, which is narrower than
            "K Looking" wants to be: it clipped before min-w-0 and wrapped
            awkwardly after. Rows read cleanly at any width and leave room to
            add a fourth metric later. */}
        <div className="mt-3 flex flex-col">
          {[
            { v: fmtStat(t.contactPct, 'pct'), l: 'Contact' },
            { v: fmtStat(t.psPa, 'ratio1'), l: 'Pitch / PA' },
            { v: fmtStat(t.kLookingShare, 'pct'), l: 'K looking' },
          ].map((m, i) => (
            <div
              key={m.l}
              className="flex items-baseline justify-between py-1.5"
              style={i > 0 ? { borderTop: `1px solid ${GS.line}` } : undefined}
            >
              <span className="text-[10px] font-extrabold uppercase" style={{ color: GS.ink3, letterSpacing: '0.06em' }}>{m.l}</span>
              <span className="text-base font-black tabular-nums" style={{ color: m.v === '—' ? GS.ink3 : GS.ink }}>{m.v}</span>
            </div>
          ))}
        </div>
      </Tile>
    </div>
  );
};
